import React, { createContext, useContext, useState, useEffect } from 'react';

interface LanguageTranslations {
  [key: string]: string;
}

interface LanguageConfig {
  name: string;
  direction: string;
  font?: string;
  translations: LanguageTranslations;
}

// Base translations object with all possible keys
const baseTranslations = {
  // Navigation
  'Get Started': '',
  'Sign in': '',
  'Solutions': '',
  'Resources': '',
  'About': '',
  'Pricing': '',

  // Hero Section
  'Meet Spark – Your School\'s New Superpower': '',
  'AI that gives time back to teachers and opportunity back to students.': '',

  // Features
  'For Educators': '',
  'For Students': '',
  'For Schools': '',
  'Take back your evenings. Let AI handle the busywork.': '',
  'Get personalized support. No more feeling lost.': '',
  'Automate admin tasks. Focus on growth, not paperwork.': '',

  // Footer
  'Documentation': '',
  'Blog': '',
  'Community': '',
  'Help Center': '',
  'Privacy & Security': '',
  'Terms': '',
  'Accessibility': '',
  'The most intelligent platform empowering schools': '',

  // Add all other strings that need translation
};

export const languageConfig: Record<string, LanguageConfig> = {
  en: {
    name: 'English',
    direction: 'ltr',
    font: "'Inter', sans-serif",
    translations: {
      // Navigation & Common
      'Get Started': 'Get Started',
      'Sign in': 'Sign in',
      'Solutions': 'Solutions',
      'Resources': 'Resources',
      'About': 'About',
      'Pricing': 'Pricing',
      'Documentation': 'Documentation',
      'Blog': 'Blog',
      'Community': 'Community',
      'Help Center': 'Help Center',

      // Hero Section
      'Meet Spark – Your School\'s New Superpower': 'Meet Spark – Your School\'s New Superpower',
      'AI that gives time back to teachers and helps students achieve more.': 'AI that gives time back to teachers and helps students achieve more.',
      'Trusted by 100+ Schools': 'Trusted by 100+ Schools',
      'FERPA Compliant': 'FERPA Compliant',
      'Save 15+ Hours Weekly': 'Save 15+ Hours Weekly',

      // Features Section
      'Transform Education with Spark': 'Transform Education with Spark',
      'For Educators': 'For Educators',
      'For Students': 'For Students',
      'For Schools': 'For Schools',
      'Take back your evenings. Let AI handle the busywork.': 'Take back your evenings. Let AI handle the busywork.',
      'Get personalized support. No more feeling lost.': 'Get personalized support. No more feeling lost.',
      'Automate admin tasks. Focus on growth, not paperwork.': 'Automate admin tasks. Focus on growth, not paperwork.',
      'AI-powered lesson planning': 'AI-powered lesson planning',
      'Automated grading & feedback': 'Automated grading & feedback',
      'Smart progress tracking': 'Smart progress tracking',
      '24/7 AI tutoring support': '24/7 AI tutoring support',
      'Personalized learning paths': 'Personalized learning paths',
      'Interactive study materials': 'Interactive study materials',
      'Comprehensive analytics': 'Comprehensive analytics',
      'Resource optimization': 'Resource optimization',
      'Streamlined communication': 'Streamlined communication',

      // Problem Section
      'The Challenge Schools Face Today': 'The Challenge Schools Face Today',
      'Teachers Are Overwhelmed': 'Teachers Are Overwhelmed',
      'Students Feel Left Behind': 'Students Feel Left Behind',
      'Administrative Overload': 'Administrative Overload',

      // CTA Section
      'Ready to Spark a New Era in Education?': 'Ready to Spark a New Era in Education?',
      'Join Waitlist': 'Join Waitlist',

      // Footer
      'Privacy & Security': 'Privacy & Security',
      'Terms': 'Terms',
      'Accessibility': 'Accessibility',
      'The most intelligent platform empowering schools': 'The most intelligent platform empowering schools',
      'SparkSkool is a registered trademark of LeapTheLimit Inc.': 'SparkSkool is a registered trademark of LeapTheLimit Inc.',

      // Additional Landing Page Content
      'Teaching': 'Teaching',
      'Smart Learning': 'Smart Learning',
      'Assessment Tools': 'Assessment Tools',
      'Communication': 'Communication',
      'School Admin': 'School Admin',
      'Transform your teaching with AI': 'Transform your teaching with AI',
      'Personalized learning journey': 'Personalized learning journey',
      'Complete education platform': 'Complete education platform',
      'Flexible learning at home': 'Flexible learning at home',
      'Support and troubleshooting': 'Support and troubleshooting',
      'News and product updates': 'News and product updates',
      'Connect with other users': 'Connect with other users',
      'Guides and API references': 'Guides and API references',
      'The most intelligent platform for schools': 'The most intelligent platform for schools'
    }
  },
  ar: {
    name: 'العربية',
    direction: 'rtl',
    font: "'Noto Sans Arabic', sans-serif",
    translations: {
      // Navigation & Common
      'Get Started': 'ابدأ الآن',
      'Sign in': 'تسجيل الدخول',
      'Solutions': 'الحلول',
      'Resources': 'المصادر',
      'About': 'عن سبارك',
      'Pricing': 'الأسعار',
      'Documentation': 'التوثيق',
      'Blog': 'المدونة',
      'Community': 'المجتمع',
      'Help Center': 'مركز المساعدة',

      // Hero Section
      'Meet Spark – Your School\'s New Superpower': 'تعرف على سبارك - القوة الخارقة الجديدة لمدرستك',
      'AI that gives time back to teachers and helps students achieve more.': 'الذكاء الاصطناعي الذي يمنح الوقت للمعلمين ويساعد الطلاب على تحقيق المزيد',
      'Trusted by 100+ Schools': 'موثوق به من قبل أكثر من 100 مدرسة',
      'FERPA Compliant': 'متوافق مع معايير FERPA',
      'Save 15+ Hours Weekly': 'وفر أكثر من 15 ساعة أسبوعياً',

      // Features Section
      'Transform Education with Spark': 'حول التعليم مع سبارك',
      'For Educators': 'للمعلمين',
      'For Students': 'للطلاب',
      'For Schools': 'للمدارس',
      'Take back your evenings. Let AI handle the busywork.': 'استعد أمسياتك. دع الذكاء الاصطناعي يتولى الأعمال الروتينية',
      'Get personalized support. No more feeling lost.': 'احصل على دعم مخصص. لا مزيد من الشعور بالضياع',
      'Automate admin tasks. Focus on growth, not paperwork.': 'أتمتة المهام الإدارية. ركز على النمو، وليس الأوراق',

      // Landing Page Additions
      'The AI Co-Teacher': 'مساعدك الذكي في التدريس',
      'Built to make teaching more powerful at every level, Spark is the best way to bring AI into your school.': 'صُمم سبارك لجعل التدريس أكثر فعالية على كافة المستويات، وهو الحل الأمثل لإدخال الذكاء الاصطناعي إلى فصولك الدراسية.',
      'Start for free': 'ابدأ مجاناً',
      'Book a demo': 'احجز عرضاً توضيحياً',
      'Grade in minutes, not hours': 'أنجز التصحيح في دقائق معدودة، وليس في ساعات',
      'With Spark\'s GradeWizard, teachers can grade up to 100 papers — including handwritten answers — in just minutes': 'باستخدام أداة التصحيح الذكية من سبارك، يمكن للمعلمين تصحيح ما يصل إلى 100 ورقة، بما في ذلك الإجابات المكتوبة بخط اليد، في دقائق معدودة.',
      'Teach your way, Test your way': 'درّس بطريقتك، واختبر بطريقتك',
      'Spark creates full exams based on your objectives, language, and classroom style — without copy-pasting.': 'يُنشئ سبارك اختبارات متكاملة بناءً على أهدافك، ولغتك، وأسلوب تدريسك — دون الحاجة للبحث والنسخ واللصق.',
      'Meet Your Spark Superpowers': 'اكتشف قدرات سبارك الفائقة',
      'Spark is more than an AI — it\'s your co-teacher, your planner, your second brain.': 'سبارك يتخطى كونه مجرد أداة — إنه معلمك المساعد، ومخطط دروسك، وعقلك الثاني.',
      'SlideMaker': 'صانع العروض',
      'Transform your lessons into engaging presentations. Beautiful slides with animations, diagrams, and examples in seconds.': 'حوّل دروسك إلى عروض تقديمية جذابة. أنشئ شرائح احترافية مزودة برسوم متحركة ومخططات وأمثلة في ثوانٍ معدودة.',
      'GameMaster': 'مصمم الألعاب',
      'Create curriculum-aligned games in one click. Bring energy into your lessons — without extra prep.': 'أنشئ ألعاباً تعليمية متوافقة مع المناهج بنقرة واحدة، وأضف الحيوية إلى دروسك دون أي تحضير مسبق.',
      'AssignmentMaker': 'صانع الواجبات',
      'Auto-generate differentiated classwork. Tailored tasks for mixed abilities, topics, and learning goals.': 'أنشئ واجبات مدرسية مخصصة تلقائياً. مهام مصممة لتناسب كافة القدرات، والمواضيع، والأهداف التعليمية.',
      'Join Spark Beta': 'انضم إلى نسخة سبارك التجريبية',
      'Get early access to ': 'احصل على وصول مبكر إلى ',
      ' and help shape the future of education technology. Limited spots available for our beta program.': ' وساعد في تشكيل مستقبل تكنولوجيا التعليم. الأماكن محدودة لبرنامجنا التجريبي.',
      'Join Beta Program': 'انضم إلى البرنامج التجريبي',
      'Works with open-ended questions, handwritten examples, and even scanned papers': 'يدعم الأسئلة المقالية، الإجابات المكتوبة بخط اليد، وحتى الأوراق الممسوحة ضوئياً',
      'Upload your teacher answer sheet, PDFs, or text — Spark reads and analyzes both instantly': 'حمّل نموذج الإجابة أو ملفات PDF أو النصوص — سيقوم سبارك بقراءتها وتحليلها فوراً',
      'Approve, adjust, or edit Spark\'s feedback before exporting or saving grades': 'ألقِ نظرة على ملاحظات سبارك، عدّلها، ثم اعتمدها قبل حفظ الدرجات أو تصديرها',
      'Just type your lesson goals, topic, or skills to assess': 'ما عليك سوى تحديد أهداف الدرس، الموضوع، أو المهارات التي تود تقييمها',
      'It creates full-length questions in your style — multiple choice, open-ended, or custom formats': 'يُنشئ اختبارات كاملة ومخصصة بأسلوبك — أسئلة خيارات متعددة، مقالية، أو بتنسيقات تناسبك',
      'Edit anything, add your voice, and export. Ready to print or share': 'عدِّل أي شيء، وأضف لمستك الخاصة، وقم بالتصدير. جاهز للطباعة أو المشاركة',
      'Our goal is to break through the "teaching vs technology" divide.': 'هدفنا هو ردم الفجوة الدائمة بين "التطوير التعليمي" و "التكنولوجيا".',
      'We work to ': 'نعمل على ',
      'empower every educator': 'تمكين كل معلم',
      ' who\'s working to shape the future of education.': ' يسعى لتشكيل مستقبل التعليم.',
      'Read more about our mission': 'اكتشف المزيد عن رسالتنا',
      'Our Roadmap': 'خارطة طريقنا',
      'See the milestones that brought us here, and get a sneak peak of what\'s next.': 'تعرف على الإنجازات التي حققناها، واكتشف ما نخبئه للمستقبل.',
      'Have some feature request? ': 'هل لديك اقتراح لميزة جديدة؟ ',
      'Join our Beta': 'انضم إلى نسختنا التجريبية',
      'and tell us more!': 'وأخبرنا المزيد!',

      // Navbar Additions
      'AI': 'الذكاء الاصطناعي',
      'The AI co-teacher you\'ve always dreamed of': 'المعلم المساعد الذكي الذي طالما حلمت به',
      'Your 24/7 learning companion and support': 'رفيقك الداعم للتعلم على مدار الساعة',
      'Your 24/7 learning companion': 'رفيقك التعلم على مدار الساعة',
      'Complete education platform for institutions': 'منصة تعليم متكاملة للمؤسسات',
      'Complete education platform': 'منصة تعليم متكاملة',
      'Flexible AI-powered learning for home education': 'تعلم مرن مدعوم بالذكاء الاصطناعي للتعليم المنزلي',
      'Flexible AI-powered learning': 'تعلم مرن مدعوم بالذكاء الاصطناعي',
      'My Dashboard': 'لوحة التحكم الخاصة بي',
      'Sign In': 'تسجيل الدخول',
      'Start the Beta': 'ابدأ النسخة التجريبية',
      'Latest updates and insights': 'آخر التحديثات والأفكار',
      'Get support and guidance': 'احصل على الدعم والتوجيه',
      'Manage Subscription': 'إدارة الاشتراك',

      // Features & Benefits
      'AI-powered lesson planning': 'تخطيط الدروس بالذكاء الاصطناعي',
      'Automated grading & feedback': 'التصحيح والتغذية الراجعة الآلية',
      'Smart progress tracking': 'تتبع ذكي للتقدم',
      '24/7 AI tutoring support': 'دعم تعليمي ذكي على مدار الساعة',
      'Personalized learning paths': 'مسارات تعلم مخصصة',
      'Interactive study materials': 'مواد دراسية تفاعلية',
      'Comprehensive analytics': 'تحليلات شاملة',
      'Resource optimization': 'تحسين الموارد',
      'Streamlined communication': 'تواصل سلس',

      // UI Elements & Actions
      'Built for modern education': 'مصمم للتعليم الحديث',
      'Designed with educators': 'مصمم مع المعلمين',
      'Secure and reliable': 'آمن وموثوق به',
      'See how it works': 'شاهد كيف يعمل',
      'Watch demo': 'شاهد العرض التوضيحي',
      'Learn more': 'اعرف المزيد',
      'Start Free Trial': 'ابدأ التجربة المجانية',
      'Schedule Demo': 'احجز عرضاً توضيحياً',
      'Contact Sales': 'تواصل مع المبيعات',
      'Try Now': 'جرب الآن',
      'Book my Cal': 'احجز موعداً',
      'Homeschool': 'التعليم المنزلي',
      'The most intelligent platform for schools': 'المنصة الأكثر ذكاءً للمدارس',

      // Pricing Page
      'Simple, Transparent Pricing': 'أسعار بسيطة وشفافة',
      'Choose the perfect plan for your teaching journey.': 'اختر الخطة المثالية لرحلتك التعليمية.',
      'Monthly': 'شهرياً',
      'Yearly': 'سنوياً',
      'Save 20%': 'وفر ٢٠٪',
      'For Home': 'للمنزل',
      'For Teachers': 'للمعلمين',
      'For Schools & Districts': 'للمدارس والمديريات',
      'Perfect for parents, tutors, and homeschoolers who want to create engaging, AI-powered learning experiences.': 'مثالي للآباء والمعلمين والتعليم المنزلي الذين يرغبون في إنشاء تجارب تعليمية تفاعلية مدعومة بالذكاء الاصطناعي.',
      'AI tools to save time, reduce burnout, and enhance teaching.': 'أدوات الذكاء الاصطناعي لتوفير الوقت وتقليل الإرهاق وتحسين التعليم.',
      'Enterprise solution for institutions ready to deploy AI-powered learning at scale.': 'حل مؤسسي للمؤسسات المستعدة لنشر التعلم المدعوم بالذكاء الاصطناعي على نطاق واسع.',
      'Most Popular': 'الأكثر شعبية',
      '/month': '/ شهر',
      '/year': '/ سنة',
      'if billed monthly': 'إذا تم الدفع شهرياً',
      'Custom Pricing': 'تسعير مخصص',
      'Plan Comparison': 'مقارنة الخطط',
      'Choose the Right Plan': 'اختر الخطة المناسبة',
      'Compare features and find the perfect plan for your needs': 'قارن الميزات واعثر على الخطة المثالية لاحتياجاتك',
      'Features': 'الميزات',

      // Pricing Features table
      'AI Assistant Credits': 'أرصدة المساعد الذكي',
      'GradeWizard Pages': 'صفحات ساحر التقييم',
      'Library Storage': 'مساحة تخزين المكتبة',
      'Interactive Digital Exams': 'امتحانات رقمية تفاعلية',
      'Printable PDF Exams': 'امتحانات PDF قابلة للطباعة',
      'Interactive Learning Games': 'ألعاب تعليمية تفاعلية',
      'Support Level': 'مستوى الدعم',
      'Analytics Dashboard': 'لوحة التحليلات',

      // Feature values
      '20/month': '٢٠/شهر',
      '30/month': '٣٠/شهر',
      '500/month': '٥٠٠/شهر',
      '1,500/month': '١,٥٠٠/شهر',
      'Custom': 'مخصص',
      '1/month': '١/شهر',
      '25/month': '٢٥/شهر',
      'Unlimited': 'غير محدود',
      '10': '١٠',
      '10+': '١٠+',
      'Priority': 'أولوية',
      'Live Chat': 'دردشة حية',
      '24/7 Priority': 'أولوية ٢٤/٧',

      // Plan Names
      'Starter': 'البداية',
      'Plus': 'بلس',
      'Pro': 'برو',
      'Enterprise': 'المؤسسات',
      'SparkSkool Home': 'سبارك سكول للمنزل',

      // Plan Details
      'Up to 3 child profiles': 'ما يصل إلى ٣ ملفات شخصية للأطفال',
      '100 AI Assistant Credits/month': '١٠٠ رصيد مساعد ذكي/شهر',
      '3 Interactive Learning Games': '٣ ألعاب تعليمية تفاعلية',
      '15 Practice quizzes/month': '١٥ اختبار تدريبي/شهر',
      'GradeWizard: 20 pages/month': 'ساحر التقييم: ٢٠ صفحة/شهر',
      '5GB Library storage': '٥ غيغابايت مساحة تخزين للمكتبة',
      'Community & Knowledge Base support': 'دعم المجتمع وقاعدة المعرفة',
      'Free Forever': 'مجاني للأبد',
      '20 AI Assistant Credits/month': '٢٠ رصيد مساعد ذكي/شهر',
      'GradeWizard: 30 pages/month': 'ساحر التقييم: ٣٠ صفحة/شهر',
      'Unlimited printable PDF exams': 'امتحانات PDF قابلة للطباعة غير محدودة',
      '1 Interactive digital exam/month': '١ امتحان رقمي تفاعلي/شهر',
      'Community support': 'دعم المجتمع',
      'Advanced analytics': 'تحليلات متقدمة',
      'Everything in Starter, plus:': 'كل شيء في خطة البداية، بالإضافة إلى:',
      '300 AI Assistant Credits/month': '٣٠٠ رصيد مساعد ذكي/شهر',
      'GradeWizard: 500 pages/month': 'ساحر التقييم: ٥٠٠ صفحة/شهر',
      '25 Interactive digital exams/month': '٢٥ امتحان رقمي تفاعلي/شهر',
      '10GB Library storage': '١٠ غيغابايت مساحة تخزين للمكتبة',
      'Advanced analytics dashboard': 'لوحة تحليلات متقدمة',
      'Priority support': 'دعم ذو أولوية',
      'Everything in Plus, plus:': 'كل شيء في خطة بلس، بالإضافة إلى:',
      'Unlimited AI Assistant Credits': 'أرصدة مساعد ذكي غير محدودة',
      'GradeWizard: 1,500 pages/month': 'ساحر التقييم: ١,٥٠٠ صفحة/شهر',
      '30GB Library storage': '٣٠ غيغابايت مساحة تخزين للمكتبة',
      '10 Interactive Learning Games': '١٠ ألعاب تعليمية تفاعلية',
      'Unlimited printable & digital exams': 'امتحانات رقمية وقابلة للطباعة غير محدودة',
      'Early access to Experience Lab': 'الوصول المبكر إلى مختبر التجارب',
      'Live chat support': 'دعم الدردشة الحية',
      'For schools, districts, and institutions': 'للمدارس والمديريات والمؤسسات',
      'Everything in Pro for all staff': 'كل شيء في خطة برو لجميع الموظفين',
      'Unlimited users': 'مستخدمين غير محدودين',
      'Dedicated account manager': 'مدير حساب مخصص',
      'Custom integrations & SSO': 'تكامل مخصص وتسجيل دخول أحادي',
      'Advanced security & compliance': 'أمان وامتثال متقدم',
      'Custom training & onboarding': 'تدريب وتأهيل مخصص',
      'Priority technical support': 'دعم فني ذو أولوية',

      // FAQ
      'Have more questions?': 'هل لديك المزيد من الأسئلة؟',
      'Get answers to commonly asked questions about pricing, subscription tiers, features, best practices, how our AI works, usage, and more!': 'احصل على إجابات للأسئلة الشائعة حول الأسعار، مستويات الاشتراك، الميزات، أفضل الممارسات، كيفية عمل الذكاء الاصطناعي، الاستخدام، والمزيد!',
      'View All FAQs': 'عرض جميع الأسئلة الشائعة',

      // Cancel Subscription
      'Manage Your Subscription': 'إدارة اشتراكك',
      'Sign in to your account to manage or cancel your subscription.': 'سجل الدخول إلى حسابك لإدارة أو إلغاء اشتراكك.',
      'Sign In to Manage': 'سجل الدخول للإدارة',
      'Signed in as': 'مسجل الدخول باسم',
      'Need to cancel your subscription? Your access will continue until the end of your current billing period.': 'هل تحتاج إلى إلغاء اشتراكك؟ سيستمر وصولك حتى نهاية فترة الفوترة الحالية.',
      'Cancel Subscription': 'إلغاء الاشتراك',
      'Sign Out': 'تسجيل الخروج',
      'Cancel Subscription?': 'إلغاء الاشتراك؟',
      'Are you sure you want to cancel your subscription? Your access will continue until the end of your current billing period. You can always re-subscribe later.': 'هل أنت متأكد أنك تريد إلغاء اشتراكك؟ سيستمر وصولك حتى نهاية فترة الفوترة الحالية. يمكنك دائمًا إعادة الاشتراك لاحقًا.',
      'Keep Subscription': 'الاحتفاظ بالاشتراك',
      'Yes, Cancel': 'نعم، إلغاء',
      'Submitting...': 'جاري الإرسال...',

      // Translation context additions
      'Billed': 'يُفوتر',
      '/mo': '/شهر',
      'mo': 'شهر'
    }
  }
};

export type Language = keyof typeof languageConfig;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  direction: string;
  switchLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const getInitialLang = (): Language => {
    if (typeof window !== 'undefined') {
      // 1. Check URL path first
      const pathParts = window.location.pathname.split('/');
      const urlLang = pathParts[1];
      if (['ar', 'en'].includes(urlLang)) {
        return urlLang as Language;
      }
      // 2. Fallback to browser language
      const browserLang = navigator.language || (navigator as any).userLanguage || 'en';
      const primary = browserLang.split('-')[0].toLowerCase();
      if (primary === 'ar') return 'ar';
    }
    return 'en';
  };

  const [language, setLanguage] = useState<Language>(getInitialLang());

  const switchLanguage = (lang: Language) => {
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split('/');
    if (['ar', 'en'].includes(pathParts[1])) {
      pathParts[1] = lang;
      window.location.href = pathParts.join('/') + window.location.search;
    } else {
      const cleanPath = currentPath === '/' ? '' : currentPath;
      window.location.href = `/${lang}${cleanPath}` + window.location.search;
    }
  };

  useEffect(() => {
    const config = languageConfig[language];
    if (config) {
      document.documentElement.dir = config.direction;
      document.documentElement.lang = language;
      if (config.font) {
        document.body.style.fontFamily = config.font;
      }
    }
  }, [language]);

  const t = (key: string): string => {
    try {
      const translation = languageConfig[language]?.translations[key];
      return translation || key; // Fallback to key if translation not found
    } catch (error) {
      console.warn(`Translation missing for key: ${key}`);
      return key; // Fallback to key if any error occurs
    }
  };

  const value = {
    language,
    setLanguage,
    switchLanguage,
    t,
    direction: languageConfig[language]?.direction || 'ltr'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
}; 